/* global VXConfig, ga */
'use strict';

import { doFetch } from '../../utils/CommonUtils';
import FluxEventEmitter from '../FluxEventEmitter';
import assign from 'object-assign';
import Routes from '../../utils/Routes';
import Constants from '../Constants';

const CHANGE_EVENTS = {
	BeforeInstallPrompt: 'beforeInstallPromptChange',
	DisableBodyScroll: 'disableBodyScrollChange',
	//GuestSubMenu:        'guestSubMenu',
	HideNavbar: 'hideNavbar',
	ContentScroll: 'contentScroll',
	Menu: 'menu',
	ScrollTo: 'scrollToChange',
	NavigateTo: 'navigateTo',
};

let _beforeInstallPrompt = null;
let _beforeInstallPromptListener = null;
let _language = '';
let _platform = '';
let _isDevServer = false;
let _isEntryPage = false;
let _restoreNavbarState = false;
let _routeName = '';
let _routeArgs = {};
const _containerScrollListeners = {};
const _elementsDisablingBodyScroll = [];
const _elementsHidingNavbar = [];
const _menus = {};
const _navbarStates = {};
const _scrollToOnMount = {};

const _guestData = {
	avatar: '',
	guestName: '',
	id: null,
	isLoggedIn: false,
	maxChannelsToShow: 5,
	subMenu: {
		heights: {},
		openMenu: null,
	},
	tileGridSettings: {},
};

function _getMenuOpen(menuId) {
	menuId = menuId || null;

	let m, subMenus;
	let menuOpen = null;

	if (menuId === null) {
		for (m in _menus) {
			if (Object.hasOwn(_menus, m) && _menus[m].isOpen) {
				menuOpen = m;
			}
		}
	} else {
		subMenus = _registerMenu(menuId).subMenus;

		for (m in subMenus) {
			if (Object.hasOwn(subMenus, m) && subMenus[m].isOpen) {
				menuOpen = m;
			}
		}
	}

	return menuOpen;
}

function _initInstallPrompt() {
	// remove old listener (window based)
	if (window.a2hs) {
		window.removeEventListener('beforeinstallprompt', window.a2hs);
		window.a2hs = null;
	}

	// install new listener (store based)
	if (!_beforeInstallPromptListener) {
		_beforeInstallPromptListener = true;
		window.addEventListener('beforeinstallprompt', _setInstallPromptEvent);
	}

	_setInstallPromptEvent(VXConfig.beforeInstallPrompt);

	// unset beforeInstallPrompt on VXConfig to avoid duplicate ga tracking
	VXConfig.beforeInstallPrompt = null;
}

function _registerMenu(menuId, subMenuId) {
	subMenuId = subMenuId || null;

	if (typeof _menus[menuId] === 'undefined') {
		_menus[menuId] = {
			isOpen: false,
			subMenus: {},
		};
	}

	if (subMenuId !== null && typeof _menus[menuId].subMenus[subMenuId] === 'undefined') {
		_menus[menuId].subMenus[subMenuId] = {
			isBlocking: true,
			isOpen: false,
			data: {},
		};
	}

	return subMenuId === null ? _menus[menuId] : _menus[menuId].subMenus[subMenuId];
}

function _resolveInstallPrompt(choiceResult) {
	if (window.ga) {
		if (choiceResult.outcome === 'dismissed') {
			ga('send', 'event', 'User', 'add_to_homescreen_dismiss');
		} else {
			ga('send', 'event', 'User', 'add_to_homescreen_accept');
		}
	}

	// remove the install button since the prompt() may only be called once
	_setInstallPromptEvent(null);
}

function _setInstallPromptEvent(e) {
	_beforeInstallPrompt = e;
	VXMobileStore.emit(CHANGE_EVENTS.BeforeInstallPrompt);

	// bind resolver to userChoice
	if (_beforeInstallPrompt) {
		_beforeInstallPrompt.userChoice.then(_resolveInstallPrompt);
	}
}

const VXMobileStore = assign({}, FluxEventEmitter.prototype, {
	EVENTS: CHANGE_EVENTS,

	init: function (data) {
		_language = data.language;
		_platform = data.platform;
		_isDevServer = data.isDevServer;
		_isEntryPage = data.isEntryPage;
		setRoute(VXConfig.routeName, VXConfig.routeArgs);

		if (data.guest) {
			_guestData.avatar = data.guest.avatar;
			_guestData.guestName = data.guest.guestName;
			_guestData.id = data.guest.id;
			_guestData.isLoggedIn = true;
			_guestData.tileGridSettings = data.guest.tileGridSettings;
		}

		_initInstallPrompt();
	},

	getLanguage: function () {
		return _language;
	},

	getPlatform: function () {
		return _platform;
	},

	getMenu: function (menuId) {
		return _registerMenu(menuId);
	},

	getMenuOpen: function () {
		return _getMenuOpen();
	},

	getRouteName: function () {
		return _routeName;
	},

	getRouteArgs: function () {
		return _routeArgs;
	},

	getScrollToOnMount: function (route) {
		return _scrollToOnMount[route];
	},

	isBodyScrollDisabled: function () {
		return _elementsDisablingBodyScroll.length > 0;
	},

	isDevServer: function () {
		return _isDevServer;
	},

	isEntryPage: function () {
		return _isEntryPage;
	},

	isIOS: function () {
		return !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
	},

	isNavbarHidden: function () {
		return _elementsHidingNavbar.length > 0;
	},

	shouldRestoreNavbarState: function () {
		return _restoreNavbarState === true;
	},

	addBodyOverflowHidden: function () {
		addBodyOverflowHidden();
	},

	removeBodyOverflowHidden: function () {
		removeBodyOverflowHidden();
	},
	showNavigation: function (withEffect = false) {
		showNavigation(withEffect);
	},
	hideNavigation: function (withEffect = false) {
		hideNavigation(withEffect);
	},

	navigateTo: function (url, replace) {
		navigateTo(url, replace);
	},

	Guest: {
		getAvatar: function () {
			return _guestData.avatar;
		},

		getTileGridSettingsByType: function (type) {
			let settings = {};
			if (typeof _guestData.tileGridSettings[type] !== 'undefined') {
				settings = _guestData.tileGridSettings[type];
			}

			return settings;
		},

		storeTileGridSettingsByType: function (type, settings) {
			storeTileGridSettingsByType(type, settings);
		},
	},

	addContainerScrollListener: function (containerId, contentId, callback) {
		addContainerScrollListener(containerId, contentId, callback);
	},

	removeContainerScrollListener: function (containerId, contentId) {
		removeContainerScrollListener(containerId, contentId);
	},

	closeMenu: function (menuId) {
		toggleMenu(menuId, false);
	},

	toggleMenu: function (menuId) {
		toggleMenu(menuId, true);
	},

	toggleSubMenu: function (menuId, subMenuId, isBlocking) {
		toggleSubMenu(menuId, subMenuId, true, isBlocking);
	},

	updateSubMenu: function (menuId, subMenuId, data) {
		updateSubMenu(menuId, subMenuId, data);
	},

	disableBodyScroll: function (triggerName) {
		disableBodyScroll(triggerName);
	},

	enableBodyScroll: function (triggerName) {
		enableBodyScroll(triggerName);
	},

	hideNavbar: function (triggerName) {
		hideNavbar(triggerName);
	},

	showNavbar: function (triggerName, callback) {
		showNavbar(triggerName, callback);
	},

	storeNavbarState: function (path) {
		storeNavbarState(path);
	},

	restoreNavbarState: function (path) {
		restoreNavbarState(path);
	},

	setRestoreNavbarState: function (restore) {
		setRestoreNavbarState(restore);
	},

	setContentScroll: function (scrollPosition) {
		setContentScroll(scrollPosition);
	},

	ignoreNextScroll: function (containerId) {
		ignoreNextScroll(containerId);
	},

	scrollPageTo: function (position) {
		scrollPageTo(position);
	},

	setRoute: function (routeName, routeArgs) {
		setRoute(routeName, routeArgs);
	},

	setScrollToOnMount: function (route, position) {
		setScrollToOnMount(route, position);
	},
});

function _createContainerScrollListener(listener) {
	return () => {
		if (listener.ignoreNextScroll) {
			listener.ignoreNextScroll = false;
		} else if (!listener.timeout && _elementsDisablingBodyScroll.length === 0) {
			listener.timeout = window.setTimeout(() => {
				const curContainerHeight = listener.container.clientHeight;
				const curScrollPosition = listener.container.scrollTop;
				const prevContainerHeight = listener.containerHeight;
				const prevScrollPosition = listener.scrollPosition;

				// update scrollPosition and containerHeight
				listener.containerHeight = curContainerHeight;
				listener.scrollPosition = curScrollPosition;

				const heightDiff = prevContainerHeight - curContainerHeight;
				const scrollDiff = curScrollPosition - prevScrollPosition;
				const heightDiffAbs = Math.abs(heightDiff);
				const scrollDiffAbs = Math.abs(scrollDiff);
				let navbarToggled = false;

				// content did not scroll if only the navbar was toggled and the scroll difference is the container's height difference
				if ((heightDiff < 0 && scrollDiff < 0) || (heightDiff > 0 && scrollDiff > 0)) {
					navbarToggled = Math.abs(heightDiffAbs - scrollDiffAbs) <= 5;
				}

				if (scrollDiffAbs > 10 && !navbarToggled) {
					// scrolled up
					if (scrollDiff < 0) {
						showNavbar(listener.id);
					} else if (curScrollPosition > 50) {
						hideNavbar(listener.id);
					}

					if (listener.callback) {
						listener.callback(curScrollPosition, prevScrollPosition, listener.container.clientHeight, listener.content.clientHeight);
					}
				}

				window.clearTimeout(listener.timeout);
				listener.timeout = null;
			}, 200);
		}
	};
}

function addContainerScrollListener(containerId, contentId, callback) {
	const container = document.getElementById(containerId);
	const content = document.getElementById(contentId);
	const listenerId = containerId + ';' + contentId;

	if (container && content && !_containerScrollListeners[listenerId]) {
		const listener = {
			callback: callback,
			containerHeight: container.clientHeight,
			container: container,
			containerId: containerId,
			content: content,
			id: listenerId,
			ignoreNextScroll: false,
			scrollPosition: 0,
			timeout: null,
		};

		listener.func = _createContainerScrollListener(listener);

		container.addEventListener('scroll', listener.func);

		_containerScrollListeners[listenerId] = listener;
	}
}

function disableBodyScroll(triggerName) {
	if (_elementsDisablingBodyScroll.indexOf(triggerName) === -1) {
		_elementsDisablingBodyScroll.push(triggerName);
		VXMobileStore.emit(CHANGE_EVENTS.DisableBodyScroll);
	}
}

function enableBodyScroll(triggerName) {
	const pos = _elementsDisablingBodyScroll.indexOf(triggerName);
	if (pos > -1) {
		_elementsDisablingBodyScroll.splice(pos, 1);
		VXMobileStore.emit(CHANGE_EVENTS.DisableBodyScroll);
	}
}

function hideNavbar(triggerName) {
	if (_elementsHidingNavbar.indexOf(triggerName) === -1) {
		_elementsHidingNavbar.push(triggerName);
		VXMobileStore.emit(CHANGE_EVENTS.HideNavbar);
	}
}

function setContentScroll(scrollPosition) {
	if (typeof scrollPosition === 'number') {
		VXMobileStore.emit(CHANGE_EVENTS.ContentScroll, scrollPosition);
	}
}

function ignoreNextScroll(containerId) {
	for (const listenerId in _containerScrollListeners) {
		if (
			Object.hasOwn(_containerScrollListeners, listenerId) &&
			_containerScrollListeners[listenerId] &&
			_containerScrollListeners[listenerId].containerId === containerId
		) {
			_containerScrollListeners[listenerId].ignoreNextScroll = true;
		}
	}
}

function removeContainerScrollListener(containerId, contentId) {
	const listenerId = containerId + ';' + contentId;
	const listener = _containerScrollListeners[listenerId];
	if (typeof listener !== 'undefined') {
		listener.container.removeEventListener('scroll', listener.func);

		if (listener.timeout) {
			clearTimeout(listener.timeout);
		}

		_containerScrollListeners[listenerId] = undefined;
	}
}

function restoreNavbarState(path) {
	if (typeof _navbarStates[path] !== 'undefined') {
		_elementsHidingNavbar.length = 0;

		for (let e = 0; e < _navbarStates[path].length; e++) {
			_elementsHidingNavbar.push(_navbarStates[path][e]);
		}

		VXMobileStore.emit(CHANGE_EVENTS.HideNavbar);
	}
}

function scrollPageTo(position) {
	setTimeout(() => {
		VXMobileStore.emit(CHANGE_EVENTS.ScrollTo, position);
	}, 0);
}

function setRestoreNavbarState(restore) {
	_restoreNavbarState = restore;
}

function setRoute(routeName, routeArgs) {
	_routeName = routeName;
	_routeArgs = routeArgs;
}

function setScrollToOnMount(route, position) {
	_scrollToOnMount[route] = position;
}

function showNavbar(triggerName, callback) {
	let hasStatusChanged = false;
	if (!triggerName) {
		hasStatusChanged = _elementsHidingNavbar.length > 0;
		_elementsHidingNavbar.length = 0;
	} else {
		const pos = _elementsHidingNavbar.indexOf(triggerName);
		if (pos > -1) {
			_elementsHidingNavbar.splice(pos, 1);
			hasStatusChanged = true;
		}
	}

	if (hasStatusChanged) {
		VXMobileStore.emit(CHANGE_EVENTS.HideNavbar, callback);
	} else if (typeof callback === 'function') {
		setTimeout(callback, 0);
	}
}

function storeNavbarState(path) {
	_navbarStates[path] = _elementsHidingNavbar.slice();
}

function storeTileGridSettingsByType(type, settings) {
	doFetch(
		Routes.getRoute(Routes.Names.TILE_GRID_STORE_TILE_GRID_SETTINGS),
		{
			type: type,
			settings: JSON.stringify(settings),
		},
		Constants.HttpMethods.POST,
		true,
	).then((result) => {
		if (result.settings) {
			_guestData.tileGridSettings = result.settings;
		}
	});
}

function addBodyOverflowHidden() {
	document.querySelector('body').classList.add('hide-overflow');
}

function removeBodyOverflowHidden() {
	document.querySelector('body').classList.remove('hide-overflow');
}

function showNavigation(withEffect = false) {
	const vxnavigation = document.getElementById('vxnavigation');
	if (vxnavigation) {
		if (withEffect) {
			vxnavigation.classList.remove('vxnavigation--hide-effect');
		} else {
			vxnavigation.classList.remove('vxnavigation--hide');
		}
	}
}

function hideNavigation(withEffect = false) {
	const vxnavigation = document.getElementById('vxnavigation');
	if (vxnavigation) {
		if (withEffect) {
			vxnavigation.classList.add('vxnavigation--hide-effect');
		} else {
			vxnavigation.classList.add('vxnavigation--hide');
		}
	}
}

/**
 *
 * @param menuId
 * @param {boolean} toggle - true: toggle, false: close
 */
function toggleMenu(menuId, toggle) {
	let m, sm;

	if (menuId === null) {
		menuId = _getMenuOpen();
	}

	const menu = _registerMenu(menuId);
	const state = toggle && !menu.isOpen;

	// state = false -> close menu
	if (!state) {
		// first: check for remaining sub menus that need to be closed
		let hasOpenSubMenus = false;
		for (sm in menu.subMenus) {
			if (Object.hasOwn(menu.subMenus, sm) && menu.subMenus[sm].isOpen) {
				menu.subMenus[sm].isOpen = false;

				if (menu.subMenus[sm].isBlocking) {
					hasOpenSubMenus = true;
				}
			}
		}

		if (hasOpenSubMenus && toggle) {
			VXMobileStore.emit(CHANGE_EVENTS.Menu);
		} else if (menu.isOpen) {
			menu.isOpen = false;
			VXMobileStore.emit(CHANGE_EVENTS.Menu);
		}
	} else {
		for (m in _menus) {
			if (Object.hasOwn(_menus, m)) {
				_menus[m].isOpen = false;

				// close all submenus
				for (sm in _menus[m].subMenus) {
					if (Object.hasOwn(_menus[m].subMenus, sm)) {
						_menus[m].subMenus[sm].isOpen = false;
					}
				}
			}
		}

		menu.isOpen = true;
		VXMobileStore.emit(CHANGE_EVENTS.Menu);
	}

	if (VXMobileStore.getMenuOpen() !== null) {
		disableBodyScroll('menu');
	} else {
		enableBodyScroll('menu');
	}
}

/**
 *
 * @param menuId
 * @param subMenuId
 * @param {boolean} toggle - true: toggle, false: close
 * @param {boolean} isBlocking - true: when toggling the main menu blocking subMenus will be closed first; only when no blocking subMenus are open the main menu will be closed
 */
function toggleSubMenu(menuId, subMenuId, toggle, isBlocking) {
	if (typeof isBlocking === 'undefined') {
		isBlocking = true;
	}

	let sm, state, menu, subMenu;

	if (menuId) {
		if (subMenuId === null) {
			subMenuId = _getMenuOpen(menuId);
		}

		subMenu = _registerMenu(menuId, subMenuId);
		state = toggle && !subMenu.isOpen;

		// state = false -> close menu
		if (!state) {
			if (subMenu.isOpen) {
				subMenu.isOpen = false;
				VXMobileStore.emit(CHANGE_EVENTS.Menu);
			}
		} else {
			menu = _registerMenu(menuId);

			// close all submenus
			for (sm in menu.subMenus) {
				if (Object.hasOwn(menu.subMenus, sm)) {
					menu.subMenus[sm].isOpen = false;
				}
			}

			subMenu.isBlocking = !!isBlocking;
			subMenu.isOpen = true;
			VXMobileStore.emit(CHANGE_EVENTS.Menu);
		}
	}
}

function updateSubMenu(menuId, subMenuId, data) {
	const subMenu = _registerMenu(menuId, subMenuId);
	let hasChanged = false;

	for (const d in data) {
		if (Object.hasOwn(data, d)) {
			if (typeof subMenu.data[d] === 'undefined' || subMenu.data[d] !== data[d]) {
				hasChanged = true;
			}
			subMenu.data[d] = data[d];
		}
	}

	if (hasChanged) {
		VXMobileStore.emit(CHANGE_EVENTS.Menu);
	}
}

function navigateTo(url, replace) {
	VXMobileStore.emit(CHANGE_EVENTS.NavigateTo, url, replace);
}


if (typeof $ !== 'undefined') {
	addEventListener('popstate', () => {
		setRestoreNavbarState(true);
	});
}

export default VXMobileStore;
